import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/EditOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useCallback } from 'react';
import { DzSubmitDay1Icon } from '../../icons';
import { useStyles } from './dz-projects.styles';
import { useModalManager, useTimer } from '../../shared';
import { writeToClipboard } from '../../../utils';
import { DzContentCopyIcon } from '../../icons/dz-content-copy-icon.view';
import { ModalIDs } from '../../../const';
export const DzMenuView = ({ projectMenuInfo, setProjectMenuInfo, actions, dispatch, address, apiClient, thunks, zendesk, partner, }) => {
    const classes = useStyles();
    const handleCloseUserMenu = useCallback(() => setProjectMenuInfo(null), [setProjectMenuInfo]);
    const { openModal } = useModalManager();
    const handleSubmitDay1 = useCallback(async () => {
        setProjectMenuInfo(null);
        const result = await openModal(ModalIDs.submitDayOne, {
            addressId: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId,
            projectId: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId,
            estimatedDay1Date: (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.estimatedDay1Date) || undefined,
            apiClient,
            actions,
            dispatch,
            thunks,
            zendesk,
            partner: partner,
        });
        if (!result) {
            return;
        }
        const { updatedAddress, updatedProject } = result;
        dispatch(actions.updateSnackbar({
            type: 'success',
            text: 'Day1 has been submitted',
        }));
        if (updatedAddress) {
            dispatch({
                type: thunks.updateAddress.fulfilled.type,
                payload: [updatedAddress],
            });
        }
        else {
            dispatch({
                type: thunks.updateProject.fulfilled.type,
                payload: { projectId: updatedProject.ovprjid },
            });
        }
        if (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId) {
            dispatch(thunks.fetchOrgDialogProjects({
                ovaid: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId,
            }));
        }
    }, [
        projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId,
        projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.estimatedDay1Date,
        openModal,
        apiClient,
        dispatch,
        thunks,
    ]);
    const handleMarkAsLost = useCallback(async () => {
        setProjectMenuInfo(null);
        const result = await openModal(ModalIDs.proposalLost, {
            projectId: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId,
            apiClient,
        });
        if (!result) {
            return;
        }
        dispatch(actions.updateSnackbar({
            type: 'success',
            text: 'Proposal has been lost',
        }));
        dispatch({
            type: thunks.updateProject.fulfilled.type,
            payload: { projectId: result.ovprjid },
        });
        if (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId) {
            dispatch(thunks.fetchOrgDialogProjects({
                ovaid: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId,
            }));
        }
    }, [projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId, openModal, apiClient, partner]);
    const handleWonProposal = useCallback(async () => {
        setProjectMenuInfo(null);
        const result = await openModal(ModalIDs.proposalWon, {
            proposal: projectMenuInfo || undefined,
            brandId: (address === null || address === void 0 ? void 0 : address.ovbid) || '',
            apiClient,
            zendesk,
            dispatch,
            actions,
        });
        if (!result) {
            return;
        }
        const { updatedAddress } = result;
        dispatch(actions.addressIsUpdating(updatedAddress));
        dispatch({
            type: thunks.updateAddress.fulfilled.type,
            payload: [updatedAddress],
        });
        dispatch(actions.updateSnackbar({
            type: 'success',
            text: 'Proposal has been won',
        }));
        if (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId) {
            dispatch(thunks.fetchOrgDialogProjects({
                ovaid: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.addressId,
            }));
        }
    }, [projectMenuInfo, openModal, apiClient, dispatch, thunks, address]);
    const handleUpdateProject = useCallback(() => {
        dispatch(actions.updateProjectInfoDialogState({
            isOpen: true,
            projectData: {
                name: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.name,
                price: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.price,
                projectId: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId,
                projectStageId: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectStageId,
            },
        }));
        setProjectMenuInfo(null);
    }, [
        projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId,
        projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.name,
        projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.price,
    ]);
    const [isTimerRunning, startTimer] = useTimer();
    const handleCopyProjectID = useCallback(async () => {
        if (!(projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.projectId)) {
            return;
        }
        await writeToClipboard(projectMenuInfo.projectId, 'text/plain');
        startTimer();
    }, [projectMenuInfo, startTimer]);
    return (_jsxs(Menu, Object.assign({ open: Boolean(projectMenuInfo), anchorEl: projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.anchor, onClose: handleCloseUserMenu }, { children: [_jsxs(MenuItem, Object.assign({ onClick: handleUpdateProject }, { children: [_jsx(ListItemIcon, Object.assign({ className: classes.listItemIcon }, { children: _jsx(EditIcon, { color: "primary", fontSize: "small" }) })), _jsx(Typography, Object.assign({ variant: "inherit" }, { children: "Update Project Info" }))] })), (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.showWonProposal) && (_jsxs(MenuItem, Object.assign({ onClick: handleWonProposal }, { children: [_jsx(ListItemIcon, Object.assign({ className: classes.listItemIcon }, { children: _jsx(CheckCircleIcon, { htmlColor: "#56ad56" }) })), _jsx(Typography, Object.assign({ variant: "inherit" }, { children: "Won Proposal" }))] }))), (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.showLost) && (_jsxs(MenuItem, Object.assign({ onClick: handleMarkAsLost }, { children: [_jsx(ListItemIcon, Object.assign({ className: classes.listItemIcon }, { children: _jsx(HighlightOffIcon, { htmlColor: "#ff3737" }) })), _jsx(Typography, Object.assign({ variant: "inherit" }, { children: "Mark as Lost" }))] }))), (projectMenuInfo === null || projectMenuInfo === void 0 ? void 0 : projectMenuInfo.showSubmitDay1) && (_jsxs(MenuItem, Object.assign({ onClick: handleSubmitDay1 }, { children: [_jsx(ListItemIcon, Object.assign({ className: classes.listItemIcon }, { children: _jsx(DzSubmitDay1Icon, {}) })), _jsx(Typography, Object.assign({ variant: "inherit" }, { children: "Submit Day1" }))] }))), _jsxs(MenuItem, Object.assign({ onClick: handleCopyProjectID }, { children: [_jsx(ListItemIcon, Object.assign({ className: classes.listItemIcon }, { children: isTimerRunning ? (_jsx(CheckIcon, { fontSize: "small" })) : (_jsx(DzContentCopyIcon, { fontSize: "small" })) })), _jsx(Typography, Object.assign({ variant: "inherit" }, { children: "Copy Project ID" }))] }))] })));
};
